import { API_CONFIG } from '../config/constants';
import { COMPANY_STAGE_HEX_VALUES } from './topicStrategies/companyStage/utils';
// import { BUSINESS_MODEL_HEX_VALUES } from './topicStrategies/businessModel/utils';

// Cache for user info
let userInfoCache = null;

// In-flight request cache to prevent duplicate concurrent API calls
let inFlightRequests = new Map();

/**
 * Utility function to collapse duplicate 'fmmagic' sections into one combined section
 * @param {Array} requirements - Array of ICP requirements
 * @returns {Array} Requirements with collapsed fmmagic sections
 */
function collapseDuplicateFmmagicSections(requirements) {
  if (!requirements || !Array.isArray(requirements)) {
    return requirements;
  }

  // Find all fmmagic sections
  const fmmagicSections = requirements.filter(req => req.section_id === 'fmmagic');
  const otherSections = requirements.filter(req => req.section_id !== 'fmmagic');

  // If we have 0 or 1 fmmagic sections, no need to collapse
  if (fmmagicSections.length <= 1) {
    return requirements;
  }

  console.log(`🔧 Collapsing ${fmmagicSections.length} duplicate fmmagic sections into one`);

  // Extract bool_str values and combine with OR
  const boolStrings = fmmagicSections
    .map(section => section.bool_str)
    .filter(Boolean) // Remove empty/null bool_str values
    .map(boolStr => {
      // Clean up the bool_str - remove outer parentheses if they exist
      const cleaned = boolStr.trim();
      if (cleaned.startsWith('((') && cleaned.endsWith('))')) {
        return cleaned.slice(2, -2); // Remove outer (( and ))
      } else if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
        return cleaned.slice(1, -1); // Remove outer ( and )
      }
      return cleaned;
    })
    .filter(Boolean); // Remove empty strings after cleaning

  if (boolStrings.length === 0) {
    console.log('🔧 No valid bool_str values found in fmmagic sections, keeping first section');
    return [...otherSections, fmmagicSections[0]];
  }

  // Combine all bool_str values with OR
  const combinedBoolStr = `((${boolStrings.join(' OR ')}))`;

  // Create the collapsed fmmagic section using the first section as template
  const collapsedFmmagicSection = {
    ...fmmagicSections[0], // Use first section as template
    bool_str: combinedBoolStr,
    section_name: fmmagicSections[0].section_name || 'Findem Magic' // Ensure we have a name
  };

  console.log(`🔧 Created collapsed fmmagic section with bool_str: ${combinedBoolStr.substring(0, 100)}...`);

  // Return other sections plus the collapsed fmmagic section
  return [...otherSections, collapsedFmmagicSection];
}

/**
 * Utility function to collapse duplicate 'skills_gh' sections into one combined section
 * @param {Array} requirements - Array of ICP requirements
 * @returns {Array} Requirements with collapsed skills_gh sections
 */
function collapseDuplicateSkillsGhSections(requirements) {
  if (!requirements || !Array.isArray(requirements)) {
    return requirements;
  }

  // Find all skills_gh sections
  const skillsGhSections = requirements.filter(req => req.section_id === 'skills_gh');
  const otherSections = requirements.filter(req => req.section_id !== 'skills_gh');

  // If we have 0 or 1 skills_gh sections, no need to collapse
  if (skillsGhSections.length <= 1) {
    return requirements;
  }

  console.log(`🔧 Collapsing ${skillsGhSections.length} duplicate skills_gh sections into one`);

  // Extract bool_str values and combine with OR
  const boolStrings = skillsGhSections
    .map(section => section.bool_str)
    .filter(Boolean) // Remove empty/null bool_str values
    .map(boolStr => {
      // Clean up the bool_str - remove outer parentheses if they exist
      const cleaned = boolStr.trim();
      if (cleaned.startsWith('((') && cleaned.endsWith('))')) {
        return cleaned.slice(2, -2); // Remove outer (( and ))
      } else if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
        return cleaned.slice(1, -1); // Remove outer ( and )
      }
      return cleaned;
    })
    .filter(Boolean); // Remove empty strings after cleaning

  if (boolStrings.length === 0) {
    console.log('🔧 No valid bool_str values found in skills_gh sections, keeping first section');
    return [...otherSections, skillsGhSections[0]];
  }

  // Combine all bool_str values with OR
  const combinedBoolStr = `((${boolStrings.join(' OR ')}))`;

  // Create the collapsed skills_gh section using the first section as template
  const collapsedSkillsGhSection = {
    ...skillsGhSections[0], // Use first section as template
    bool_str: combinedBoolStr,
    section_name: skillsGhSections[0].section_name || 'Nice to Have Skills' // Ensure we have a name
  };

  console.log(`🔧 Created collapsed skills_gh section with bool_str: ${combinedBoolStr.substring(0, 100)}...`);

  // Return other sections plus the collapsed skills_gh section
  return [...otherSections, collapsedSkillsGhSection];
}

/**
 * Fetch user information from the Findem API with caching
 * @returns {Promise<Object>} User information
 */
export async function fetchUserInfo() {
  // Use deduplication wrapper to prevent concurrent duplicate calls
  return withDeduplication('fetchUserInfo', async () => {
    // Return cached user info if available
    if (userInfoCache) {
      console.log('Using cached user info');
      return userInfoCache;
    }

    try {
      console.log('Fetching user info from API');
      const response = await fetch('https://matches.findem.ai/api/user_info', {
        method: 'GET',
        credentials: 'include',
        redirect: 'follow', // Ensure the browser automatically follows redirects
      });

      if (response.redirected) {
        // Check if the redirect URL is the login page
        if (response.url.includes('/auth/login')) {
          window.location.href = 'https://matches.findem.ai/auth/login'; // Manually redirect
          throw new Error('User is not authenticated. Redirected to login page.');
        }
      }

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      
      // Store the user info in cache
      userInfoCache = await response.json();
      return userInfoCache;
    } catch (error) {
      console.error('Error fetching user_info:', error.message);
      return null;
    }
  });
}

let cache = {}

// Cache for queries
let queriesCache = null;

/**
 * Deduplication wrapper for API calls
 * @param {string} key - Unique key for the request
 * @param {Function} apiCall - The actual API call function
 * @returns {Promise} - The API response
 */
async function withDeduplication(key, apiCall) {
  // Check if there's already an in-flight request for this key
  if (inFlightRequests.has(key)) {
    console.log(`Deduplicating API call for key: ${key.substring(0, 100)}...`);
    return inFlightRequests.get(key);
  }

  // Create the promise and store it in the in-flight requests
  const promise = apiCall().finally(() => {
    // Remove from in-flight requests when completed (success or error)
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, promise);
  return promise;
}

/**
 * Fetch queries from the Findem API with caching
 * @returns {Promise<Object>} The fetched queries
 */
export async function fetchQueries() {
  // Use deduplication wrapper to prevent concurrent duplicate calls
  return withDeduplication('fetchQueries', async () => {
    try {
      // Return cached queries if available
      if (queriesCache) {
        console.log('Using cached queries');
        return queriesCache;
      }

      console.log('Fetching queries from API');
      const response = await fetch(
        "https://search.findem.ai/pub/api/fetch_queries",
        {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({ only_user: true }),
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      
      // Store the queries in cache
      queriesCache = await response.json();
      return queriesCache;
    } catch (error) {
      console.error("Error fetching queries:", error.message);
      return null;
    }
  });
}

// Global store for the selected company domain
let selectedCompanyDomain = null;

// Global store for the selected company stages
let selectedCompanyStages = [];

// Global store for the selected business models
let selectedBusinessModels = [];

// Function to set the selected company domain
export function setSelectedCompanyDomain(domain) {
  console.log('Setting selected company domain:', domain);
  selectedCompanyDomain = domain;
}

// Function to get the selected company domain
export function getSelectedCompanyDomain() {
  return selectedCompanyDomain;
}

// Function to set the selected company stages
export function setSelectedCompanyStages(stages) {
  console.log('Setting selected company stages:', stages);
  selectedCompanyStages = stages;
}

// Function to get the selected company stages
export function getSelectedCompanyStages() {
  return selectedCompanyStages;
}

// Function to set the selected business models
export function setSelectedBusinessModels(models) {
  console.log('Setting selected business models:', models);
  selectedBusinessModels = models;
}

// Function to get the selected business models
export function getSelectedBusinessModels() {
  return selectedBusinessModels;
}

// Helper function to generate company stage requirements based on selected stages
function generateCompanyStageRequirements() {
  if (!selectedCompanyStages || selectedCompanyStages.length === 0) {
    return [];
  }

  // Group stages by category (CURRENT, PAST, RECENT)
  const stagesByCategory = selectedCompanyStages.reduce((acc, stage) => {
    if (!acc[stage.category]) {
      acc[stage.category] = [];
    }
    acc[stage.category].push(stage);
    return acc;
  }, {});

  const requirements = [];

  // Create requirements for each category
  Object.entries(stagesByCategory).forEach(([category, stages], index) => {
    if (stages.length > 0) {
      // Get hex values for the selected stages in this category
      const hexValues = stages.map(stage => {
        const hexKey = `${stage.category}_${stage.value.toUpperCase()}`;
        return COMPANY_STAGE_HEX_VALUES[hexKey];
      }).filter(Boolean);

      if (hexValues.length > 0) {
        // Combine hex values with OR operators
        const boolStr = `(${hexValues.map(hex => hex).join(' OR ')})`;
        
        // Create the requirement object
        const requirement = {
          bool_str: boolStr,
          section_id: `${category.toLowerCase()}_company_stage`,
          section_name: `${category.charAt(0) + category.slice(1).toLowerCase()} Company Stage`,
          section_order: index + 2, // Start from 2 to avoid conflicts with other requirements
          section_type: "Must Have"
        };
        
        requirements.push(requirement);
      }
    }
  });

  return requirements;
}

// Helper function to generate business model requirements based on selected business models
function generateBusinessModelRequirements() {
  return [];

  // if (!selectedBusinessModels || selectedBusinessModels.length === 0) {
  //   return [];
  // }

  // // Group business models by category (CURRENT, PAST, RECENT)
  // const modelsByCategory = selectedBusinessModels.reduce((acc, model) => {
  //   if (!acc[model.category]) {
  //     acc[model.category] = [];
  //   }
  //   acc[model.category].push(model);
  //   return acc;
  // }, {});

  // const requirements = [];

  // // Create requirements for each category
  // Object.entries(modelsByCategory).forEach(([category, models], index) => {
  //   if (models.length > 0) {
  //     // Get hex values for the selected business models in this category
  //     const hexValues = models.map(model => {
  //       const hexKey = `${model.category}_${model.value.toUpperCase()}`;
  //       return BUSINESS_MODEL_HEX_VALUES[hexKey];
  //     }).filter(Boolean);

  //     if (hexValues.length > 0) {
  //       // Combine hex values with OR operators
  //       const boolStr = `(${hexValues.map(hex => hex).join(' OR ')})`;
        
  //       // Create the requirement object
  //       const requirement = {
  //         bool_str: boolStr,
  //         section_id: `${category.toLowerCase()}_company_biz_modal`,
  //         section_name: `${category.charAt(0) + category.slice(1).toLowerCase()} Business Model`,
  //         section_order: index + 2, // Start from 2 to avoid conflicts with other requirements
  //         section_type: "Must Have"
  //       };
        
  //       requirements.push(requirement);
  //     }
  //   }
  // });

  // return requirements;
}

export async function runAIMatches (messages, options = {}) {
  // Determine intake type priority:
  // 1) Explicit option from caller
  // 2) Global/window flag set by AppLayout when JD or ElevenLabs was chosen
  // 3) localStorage fallback (in case of reload)
  // 4) Default to 'prompt'
  let intakeType = options?.intakeType;
  if (!intakeType && typeof window !== 'undefined') {
    const globalChoice = window.__intakeChoice;
    if (globalChoice === 'jd' || globalChoice === 'elevenlabs') {
      intakeType = 'jd';
    }
  }
  if (!intakeType && typeof localStorage !== 'undefined') {
    try {
      const storedChoice = localStorage.getItem('intakeChoice');
      if (storedChoice === 'jd' || storedChoice === 'elevenlabs') {
        intakeType = 'jd';
      }
    } catch (_) {}
  }
  if (!intakeType) intakeType = 'prompt';

  // JD path: use Copilot JD SearchGen and normalize to search_gen_response
  if (intakeType === 'jd') {
    // Derive JD source text
    const jdSource =
      options.jdText && typeof options.jdText === 'string' ? options.jdText :
      (typeof messages === 'string' ? messages :
        (Array.isArray(messages)
          ? messages
              .map(m => {
                const role = (m && m.role) ? (m.role === 'AI' ? 'Agent' : 'User') : 'User';
                const content = (m && m.content) ? String(m.content) : '';
                return `${role}: ${content}`;
              })
              .join('\n')
          : JSON.stringify(messages)));

    const jdPayload = {
      jd_search_gen_info: {
        verbose: false,
        jd_source: jdSource || ''
      },
      reset_context: true
    };

    const payload_str = JSON.stringify({ intakeType, jdPayload });

    // Use deduplication wrapper
    return withDeduplication(`runAIMatches_${payload_str}`, async () => {
      try {
        // Cache hit
        if (cache[payload_str]) {
          console.log('Using cached runAIMatches (JD) result');
          return cache[payload_str];
        }

        console.log('runAIMatches (JD) payload:', {
          jd_len: (jdSource || '').length,
          reset_context: true
        });

        // JD API URL (as provided in product request)
        const url = 'https://search.findem.ai/pub/api/copilot-jd-searchgen';

        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include', // keep consistent with other authenticated calls
          headers: { 'Content-type': 'application/json' },
          body: JSON.stringify(jdPayload),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`runAIMatches (JD) failed: ${response.status} ${response.statusText}`);
        }

        let result = await response.json();

        // Normalize to common envelope if needed
        if (!result.search_gen_response && result.jd_search_gen_response) {
          result.search_gen_response = result.jd_search_gen_response;
        }

        // Apply same Companies section tweak for consistency
        try {
          result.search_gen_response?.icp_requirements?.forEach((req) => {
            if (req.section_name && req.section_name.indexOf('Companies') >= 0 && req.section_type === 'Must Have') {
              req.section_type = 'Nice to Have';
            }
          });
        } catch (_) {}

        cache[payload_str] = result;
        return result;
      } catch (error) {
        console.error('Error in runAIMatches (JD):', error);
        throw error;
      }
    });
  }

  // Prompt path (default): use Copilot SearchGen
  // Prepare questions and payload
  let questions = [JSON.stringify(messages)];
  let payload = {
    qna_search_gen_info: {
      questions: questions,
      v2: true
    },
    reset_context: false // preserve context
  };

  let payload_str = JSON.stringify({ intakeType: 'prompt', payload });

  // Use deduplication wrapper
  return withDeduplication(`runAIMatches_${payload_str}`, async () => {
    try {
      console.log('runAIMatches (prompt) payload:', {
        questions: questions.length,
        reset_context: false
      });

      if (cache[payload_str]) {
        console.log('Using cached runAIMatches (prompt) result');
        return cache[payload_str];
      }

      const url = `https://pub.findem.ai/pub/api/copilot-searchgen`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`runAIMatches failed: ${response.status} ${response.statusText}`);
      }

      let result = await response.json();

      try {
        result.search_gen_response?.icp_requirements?.forEach((req) => {
          if (req.section_name && req.section_name.indexOf('Companies') >= 0 && req.section_type === 'Must Have') {
            req.section_type = 'Nice to Have';
          }
        });
      } catch (_) {}

      cache[payload_str] = result;
      return result;
    } catch (error) {
      console.error('Error in runAIMatches:', error);
      throw error;
    }
  });
}

export async function runMultiMatches (reqs, to_match, to_match_requirements, options = {}) {
  // Create deduplication key based on parameters
  const payload = {
    requirements: reqs,
    to_match: to_match,
    to_match_requirements: to_match_requirements,
    options: options
  };
  const deduplicationKey = `runMultiMatches_${JSON.stringify(payload)}`;

  // Use deduplication wrapper to prevent concurrent duplicate calls
  return withDeduplication(deduplicationKey, async () => {
    try {
      // Create copies of the requirements to avoid modifying the originals
      let requirements = [...reqs];
      let toMatchReqs = [...to_match_requirements];
      
      // Collapse duplicate fmmagic and skills_gh sections in requirements
      requirements = collapseDuplicateFmmagicSections(requirements);
      requirements = collapseDuplicateSkillsGhSections(requirements);
      
      // Collapse duplicate fmmagic and skills_gh sections in to_match_requirements
      toMatchReqs = collapseDuplicateFmmagicSections(toMatchReqs);
      toMatchReqs = collapseDuplicateSkillsGhSections(toMatchReqs);
      
      // Add company stage requirements if available
      const companyStageReqs = generateCompanyStageRequirements();
      if (companyStageReqs.length > 0) {
        console.log('Adding company stage requirements to multiMatches:', companyStageReqs);
        requirements = [...requirements, ...companyStageReqs];
      }
      
      // Add business model requirements if available
      const businessModelReqs = generateBusinessModelRequirements();
      if (businessModelReqs.length > 0) {
        console.log('Adding business model requirements to multiMatches:', businessModelReqs);
        requirements = [...requirements, ...businessModelReqs];
      }
      
      // Check if we need to run on self org
      if (options.run_on_self_org) {
        // Check if we have a selected company domain from the header
        if (selectedCompanyDomain) {
          // Use the selected company domain instead of org preferences
          console.log("Using selected company domain for multimatches:", selectedCompanyDomain);
          
          // Build the bool_str for the selected domain
          let domainBoolStr = `(workexp.current.company.domain: ${selectedCompanyDomain})`;
          
          console.log("include domain for multimatches: " + domainBoolStr);
          
          // Create the domain requirement
          const domainRequirement = {
            "bool_str": `${domainBoolStr}`,
            "section_id": "company_by_domain",
            "section_name": "Current Company By Domain",
            "section_order": 2,
            "section_type": "Must Have"
          };
          
          // Add to both requirements and to_match_requirements
          requirements.push(domainRequirement);
          // toMatchReqs.push(domainRequirement);
          
          console.log('Added domain inclusion for selected company:', selectedCompanyDomain);
        }
        // If no selected domain, fall back to org preferences
        else if (!orgPreferencesCache) {
          console.log('Fetching org preferences for domain exclusion...');
          orgPreferencesCache = await fetchOrgPreferences();
        }
        
        // If we have org preferences with domain exclusion and no selected company domain
        if (!selectedCompanyDomain && orgPreferencesCache && orgPreferencesCache.domain_to_exclude_search) {
          // Parse the comma-separated domains
          const domains = orgPreferencesCache.domain_to_exclude_search.split(',').map(domain => domain.trim());
          
          // Build the bool_str for domain exclusion
          let domainBoolStr = '(' + domains.map(domain => {
            return `workexp.current.company.domain: ${domain}`;
          }).join(' OR ') + ')'; 
          
          console.log("include domains from org prefs for multimatches: " + domainBoolStr);

          // Create the domain requirement
          const domainRequirement = {
            "bool_str": `(${domainBoolStr})`,
            "section_id": "company_by_domain",
            "section_name": "Current Company By Domain",
            "section_order": 2,
            "section_type": "Must Have"
          };
          
          // Add to both requirements and to_match_requirements
          requirements.push(domainRequirement);
          // toMatchReqs.push(domainRequirement);
          
          console.log('Added domain inclusion for multimatches:', domains.join(', '));
        }
      }
      
      // Prepare payload with potentially modified requirements
      let finalPayload = {
        requirements: requirements,
        to_match: to_match,
        to_match_requirements: toMatchReqs
      }
      let payload_str = JSON.stringify(finalPayload)
      if (cache[payload_str]) {
        return cache[payload_str]
      }

      // API URL
      let url = `https://pub.findem.ai/pub/api/multi_matchesv2`
      // Make the fetch call with credentials included
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Ensure cookies are sent with the request
        body: JSON.stringify(finalPayload),
        headers: {
          'Content-type': 'application/json'
        },
        mode: 'cors'
      })

      // Check for successful response
      if (!response.ok) {
        throw new Error(
          `runMultiMatches failed: ${response.status} ${response.statusText}`
        )
      }

      // Parse and return the JSON response
      const result = await response.json()
      cache[payload_str] = result
      return result
    } catch (error) {
      console.error('Error in runMultiMatches:', error)
      throw error
    }
  });
}

// Cache for org preferences
let orgPreferencesCache = null;

/**
 * Clear all client-side API caches and transient selections.
 * This prevents stale data (e.g., JD SearchGen results and multi-matches) from being reused
 * after a cache/session clear within the SPA lifecycle.
 */
export function clearClientCaches() {
  try { userInfoCache = null; } catch (_) {}
  try { if (inFlightRequests && typeof inFlightRequests.clear === 'function') inFlightRequests.clear(); } catch (_) {}
  try { cache = {}; } catch (_) {}
  try { queriesCache = null; } catch (_) {}
  try { orgPreferencesCache = null; } catch (_) {}
  try { selectedCompanyDomain = null; } catch (_) {}
  try { selectedCompanyStages = []; } catch (_) {}
  try { selectedBusinessModels = []; } catch (_) {}
  try { console.log('🧼 API: Cleared client caches (userInfo, inFlightRequests, memoized results, org prefs, selections)'); } catch (_) {}
}

export async function runMatchesAnalyze (reqs, options = {}) {
  // Create deduplication key based on parameters
  const payload = {
    requirements: reqs,
    options: options
  };
  const deduplicationKey = `runMatchesAnalyze_${JSON.stringify(payload)}`;

  // Use deduplication wrapper to prevent concurrent duplicate calls
  return withDeduplication(deduplicationKey, async () => {
    try {
      // Create a copy of the requirements to avoid modifying the original
      let modifiedReqs = [...reqs];
      
      // Collapse duplicate fmmagic and skills_gh sections
      modifiedReqs = collapseDuplicateFmmagicSections(modifiedReqs);
      modifiedReqs = collapseDuplicateSkillsGhSections(modifiedReqs);
      
      // Add company stage requirements if available
      const companyStageReqs = generateCompanyStageRequirements();
      if (companyStageReqs.length > 0) {
        console.log('Adding company stage requirements to matchesAnalyze:', companyStageReqs);
        modifiedReqs = [...modifiedReqs, ...companyStageReqs];
      }
      
      // Add business model requirements if available
      const businessModelReqs = generateBusinessModelRequirements();
      if (businessModelReqs.length > 0) {
        console.log('Adding business model requirements to matchesAnalyze:', businessModelReqs);
        modifiedReqs = [...modifiedReqs, ...businessModelReqs];
      }
      
      // Check if we need to run on self org
      if (options.run_on_self_org) {
        // Check if we have a selected company domain from the header
        if (selectedCompanyDomain) {
          // Create a copy of the requirements to avoid modifying the original
          const reqsWithDomainInclusion = [...modifiedReqs];
          
          // Use the selected company domain instead of org preferences
          console.log("Using selected company domain for matches analyze:", selectedCompanyDomain);
          
          // Build the bool_str for the selected domain
          let domainBoolStr = `(workexp.current.company.domain: ${selectedCompanyDomain})`;
          
          console.log("include domain for matches analyze: " + domainBoolStr);
          
          // Add the domain requirement
          reqsWithDomainInclusion.push({
            bool_str: `${domainBoolStr}`,
            section_id: "internal_employee_type",
            section_type: "Must Have"
          });

          // Use the modified requirements
          modifiedReqs = reqsWithDomainInclusion;
          console.log('Added domain inclusion for selected company:', selectedCompanyDomain);
        }
        // If no selected domain, fall back to org preferences
        else {
          // Get org preferences (from cache if available)
          if (!orgPreferencesCache) {
            console.log('Fetching org preferences for domain exclusion...');
            orgPreferencesCache = await fetchOrgPreferences();
          }
          
          // If we have org preferences with domain exclusion
          if (orgPreferencesCache && orgPreferencesCache.domain_to_exclude_search) {
            // Create a copy of the requirements to avoid modifying the original
            const reqsWithDomainInclusion = [...modifiedReqs];
            
            // Parse the comma-separated domains
            const domains = orgPreferencesCache.domain_to_exclude_search.split(',').map(domain => domain.trim());
            
            // Build the bool_str for domain exclusion
            let domainBoolStr = '(' + domains.map(domain => {
              return `workexp.current.company.domain: ${domain}`;
            }).join(' OR ') + ')'; 
            
            console.log("include domains from org prefs: " + domainBoolStr);

            // Add the domain exclusion requirement
            reqsWithDomainInclusion.push({
              bool_str: `(${domainBoolStr})`,
              section_id: "internal_employee_type",
              section_type: "Must Have"
            });

            // Use the modified requirements
            modifiedReqs = reqsWithDomainInclusion;
            console.log('Added domain inclusion for:', domains.join(', '));
          }
        }
      }
      
      // If centralized ICP requirements are provided, use them instead
      if (options.icpRequirements && Array.isArray(options.icpRequirements) && options.icpRequirements.length > 0) {
        console.log('Using centralized ICP requirements for matches analyze:', options.icpRequirements.length);
        modifiedReqs = options.icpRequirements;
        
        // Add company stage requirements to centralized ICP requirements if available
        if (companyStageReqs.length > 0) {
          modifiedReqs = [...modifiedReqs, ...companyStageReqs];
        }
        
        // Add business model requirements to centralized ICP requirements if available
        if (businessModelReqs.length > 0) {
          modifiedReqs = [...modifiedReqs, ...businessModelReqs];
        }
      }
      
      // Prepare questions and payload
      let finalPayload = {
        requirements: modifiedReqs,
        count: 10000,
        detail: false,
        atlas: false,
        tenure_cid: [],
        sandbox: false,
        consider_current_comp_prev_role: true,
        v2_fields: true
      }

      let payload_str = JSON.stringify(finalPayload)
      if (cache[payload_str]) {
        return cache[payload_str]
      }

      // API URL
      let url = `https://pub.findem.ai/pub/api/matches_analyze_v2`
      // Make the fetch call with credentials included
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Ensure cookies are sent with the request
        body: JSON.stringify(finalPayload),
        headers: {
          'Content-type': 'application/json'
        },
        mode: 'cors'
      })

      // Check for successful response
      if (!response.ok) {
        throw new Error(
          `runMatchesAnalyze failed: ${response.status} ${response.statusText}`
        )
      }

      // Parse and return the JSON response
      const result = await response.json()
      cache[payload_str] = result
      return result
    } catch (error) {
      console.error('Error in runMatchesAnalyze:', error)
      throw error
    }
  });
}

export async function fetchSSE (
  url,
  options,
  onMessage,
  isAudioReady,
  currentSessionId,
  userId,
  tempMessageId,
  retryCount = 0,
  maxRetries = 3,
  usePythonBackend = false,
  useEnhanced = true
) {
  // If using Python backend, modify the URL
  if (usePythonBackend) {
    url = url.replace('/api/send', '/api/send-python');
    console.log(`Using Python backend: ${url}`);
  }

  const parsedBody = JSON.parse(options.body || '{}');
  const messages = parsedBody.messages || [];
  try {
    // Add a flag to indicate that the server should use the chat history from the database
    // This is important for ensuring that the entire chat history is sent to the LLM
    const useServerChatHistory = currentSessionId ? true : false;
    
    console.log(`API: Using server chat history: ${useServerChatHistory}, Session ID: ${currentSessionId}`);
    
    // Notify the user that we're connecting
    if (retryCount > 0) {
      onMessage({
        type: 'connection_warning',
        message: `Connecting to server (attempt ${retryCount + 1}/${maxRetries + 1})...`
      }, isAudioReady);
    }
    
    const response = await fetch(url, {
      ...options,
      body: JSON.stringify({
        ...JSON.parse(options.body || '{}'),
        userID: userId, // Add userID here
        isAudioReady: isAudioReady,
        sessionId: currentSessionId,
        isInitialMessage: messages.length === 0, // Add a flag to indicate the initial message
        roleTypes: messages.length === 0 && JSON.parse(options.body || '{}').roleTypes ? JSON.parse(options.body || '{}').roleTypes : null,
        messages: messages,
        useServerChatHistory: useServerChatHistory, // Add flag to use server chat history
        useEnhanced: useEnhanced // Add flag to use enhanced mode
      })
    });

    if (!response.ok) {
      const errorMessage = `fetchSSE failed: ${response.status} ${response.statusText}`;
      console.error(errorMessage);
      
      // Notify the user about the error
      onMessage({
        type: 'connection_error',
        message: `Error connecting to server: ${response.status} ${response.statusText}`
      }, isAudioReady);
      
      throw new Error(errorMessage);
    }

    // Notify the user that we're connected
    onMessage({
      type: 'connection_restored',
      message: 'Connected to server successfully.'
    }, isAudioReady);

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let lastActivityTime = Date.now();
    const idleTimeout = 30000; // 30 seconds idle timeout
    
    // Set up a heartbeat to check for idle connections
    const heartbeatInterval = setInterval(() => {
      const currentTime = Date.now();
      if (currentTime - lastActivityTime > idleTimeout) {
        console.warn('SSE connection idle for too long, reconnecting...');
        clearInterval(heartbeatInterval);
        
        // Notify the user about the idle connection
        onMessage({
          type: 'connection_warning',
          message: 'Connection idle for too long, reconnecting...'
        }, isAudioReady);
        
        // Close the current reader and retry
        reader.cancel('Connection idle timeout');
        
        // Retry with exponential backoff
        const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 10000);
        setTimeout(() => {
          fetchSSE(url, options, onMessage, isAudioReady, currentSessionId, userId, tempMessageId, retryCount + 1, maxRetries, usePythonBackend)
            .catch(err => {
              console.error('Error reconnecting SSE:', err);
              
              // Notify the user about the reconnection error
              onMessage({
                type: 'connection_error',
                message: `Error reconnecting: ${err.message}`
              }, isAudioReady);
            });
        }, backoffTime);
      }
    }, 5000); // Check every 5 seconds

    try {
      while (true) {
        const { value, done } = await reader.read();
        
        if (done) {
          console.log('SSE stream closed by server');
          break;
        }
        
        // Update last activity time
        lastActivityTime = Date.now();
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        let boundary;
        while ((boundary = buffer.indexOf('\n\n')) >= 0) {
          const sseEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);

          const lines = sseEvent.split('\n');
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const jsonString = line.slice(5).trim();
              try {
                const event = JSON.parse(jsonString);
                // Add tempMessageId to the event if it exists
                if (tempMessageId && !event.tempMessageId) {
                  event.tempMessageId = tempMessageId;
                }
                onMessage(event, isAudioReady);
              } catch (err) {
                console.error('Failed to parse SSE JSON:', err, jsonString);
              }
            }
          }
        }
      }
    } finally {
      clearInterval(heartbeatInterval);
    }
  } catch (error) {
    console.error('SSE connection error:', error);
    
    // Notify the user about the error
    onMessage({
      type: 'connection_error',
      message: `Connection error: ${error.message}`
    }, isAudioReady);
    
    // Retry logic with exponential backoff
    if (retryCount < maxRetries) {
      const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 10000);
      console.log(`Retrying SSE connection in ${backoffTime}ms (attempt ${retryCount + 1}/${maxRetries})...`);
      
      // Notify the user about the retry
      onMessage({
        type: 'connection_warning',
        message: `Retrying connection in ${Math.round(backoffTime / 1000)} seconds (attempt ${retryCount + 1}/${maxRetries})...`
      }, isAudioReady);
      
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(fetchSSE(url, options, onMessage, isAudioReady, currentSessionId, userId, tempMessageId, retryCount + 1, maxRetries, usePythonBackend));
        }, backoffTime);
      });
    } else {
      console.error('Max SSE retry attempts reached');
      
      // Notify the user that we've reached the maximum number of retries
      onMessage({
        type: 'connection_failed',
        message: 'Failed to connect after multiple attempts. Please try again later.'
      }, isAudioReady);
      
      throw error;
    }
  }
}

export async function fetchIndustries (matches, queries) {
  try {
    const response = fetch('https://search.findem.ai/pub/api/industrylist', {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })
    // Check for successful response
    if (!response.ok) {
      throw new Error(
        `runAIMatches failed: ${response.status} ${response.statusText}`
      )
    }

    // Parse and return the JSON response
    const result = await response.json()
    return result
  } catch (error) {
    console.error('Error in fetchIndustries:', error)
    throw error
  }
}

export async function fetchChatCompletion (apiUrl, userInput, agentInput) {
  let body = {
    userInput: userInput,
    agentInput: agentInput
  }
  let url = apiUrl + '/api/chat-completion'
  
  // Log the chat completion request for debugging
  console.log('CHAT COMPLETION REQUEST:', {
    url,
    userInput: userInput.substring(0, 100) + (userInput.length > 100 ? '...' : ''),
    agentInput
  });
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }

    const data = await response.json()
    
    // Log the full chat completion response for debugging
    console.log('CHAT COMPLETION RESPONSE:', {
      completion: data.completion,
      type: typeof data.completion
    });
    
    // Check if the completion is a simple string like "Yes" or "No"
    if (typeof data.completion === 'string') {
      try {
        // Try to parse as JSON first
        const parsedCompletion = JSON.parse(data.completion);
        console.log('PARSED COMPLETION:', parsedCompletion);
        return parsedCompletion;
      } catch (parseError) {
        // If parsing fails, it's likely a simple string response, so return it directly
        console.log('Returning completion as string:', data.completion);
        return data.completion;
      }
    }
    
    return data.completion;
  } catch (error) {
    console.error('Error fetching chat completion:', error)
    return null
  }
}

export async function transcribeAudio(audioBlob) {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const response = await fetch(`${API_CONFIG.url}/api/transcribe`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Transcription API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
}

export async function saveIcp (name, icpid, requirements, retries = 3) {
  const url = 'https://search.findem.ai/pub/api/icp'

  // Add company stage requirements if available
  const companyStageReqs = generateCompanyStageRequirements();
  if (companyStageReqs.length > 0) {
    console.log('Adding company stage requirements to saveIcp:', companyStageReqs);
    requirements = [...requirements, ...companyStageReqs];
  }
  
  // Add business model requirements if available
  const businessModelReqs = generateBusinessModelRequirements();
  if (businessModelReqs.length > 0) {
    console.log('Adding business model requirements to saveIcp:', businessModelReqs);
    requirements = [...requirements, ...businessModelReqs];
  }

  const headers = {
    'content-type': 'application/json'
  }

  const body = {
    icpid: icpid,
    name: name,
    type: 'CreateUpdateV2',
    description: '',
    requirements: requirements
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(
        `Failed to save ICP: ${response.status} ${response.statusText}`
      )
    }

    const data = await response.json()
    console.log('ICP saved successfully:', data)
    return data
  } catch (error) {
    console.error('Error saving ICP:', error)

    if (retries > 0) {
      console.log(`Retrying... (${3 - retries + 1} attempt(s) left)`)
      return saveIcp(name, null, requirements, retries - 1) // Retry with one less attempt
    }

    console.error('All retries failed. Unable to save ICP.')
    throw error // Re-throw the error after all retries have failed
  }
}

// Role Taxonomy API Functions

/**
 * Fetch all role types from the server
 * @returns {Promise<Object>} Object containing roleTypes array, advancedConfig, and queueItemConfig
 */
export async function fetchRoleTypes() {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/role-types`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch role types: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching role types:', error);
    // Return null to indicate an error occurred
    return null;
  }
}

/**
 * Fetch queue item configuration from the server
 * @returns {Promise<Object>} Object containing queue item configurations
 */
export async function fetchQueueItemConfig() {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/queue-item-config`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch queue item config: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching queue item config:', error);
    // Return empty object to indicate an error occurred
    return {};
  }
}

/**
 * Get the intake strategy for a specific role type
 * @param {string} roleTypeId - The ID of the role type
 * @returns {Promise<Object>} Object containing the role type ID and its intake strategy
 */
export async function getIntakeStrategy(roleTypeId) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/intake-strategy/${roleTypeId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get intake strategy: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error getting intake strategy for role type ${roleTypeId}:`, error);
    // Return null to indicate an error occurred
    return null;
  }
}

/**
 * Classify a role title into a role type
 * @param {string} roleTitle - The title of the role to classify
 * @returns {Promise<Object>} Object containing the role type, classification, and intake strategy
 */
export async function classifyRole(roleTitle) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/classify-role`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ role_title: roleTitle })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to classify role: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error classifying role:', error);
    // Return null to indicate an error occurred
    return null;
  }
}

/**
 * Update discovery sequences for role types
 * @param {Object} discoverySequences - Object mapping role type IDs to their new discovery order arrays
 * @returns {Promise<Object>} Object containing the status of the update operation
 */
export async function updateDiscoverySequences(discoverySequences) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/discovery-sequences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(discoverySequences)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update discovery sequences: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating discovery sequences:', error);
    // Return null to indicate an error occurred
    return null;
  }
}

// Function to get companies by IDs
export async function getCompaniesByIds(companyIds) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/companies-by-ids`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ company_ids: companyIds })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get companies: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting companies by IDs:', error);
    return [];
  }
}

// Function to fetch company autocomplete results
export async function fetchCompanyAutocomplete(query) {
  try {
    const response = await fetch(`https://search.findem.ai/pub/api/company_ac`,     {
      method: 'POST',
      headers: {
        "Content-Type": "application/json"
      },
      credentials: 'include',
      body: JSON.stringify({
        prefix: query,
        count: 15
      })
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch company autocomplete: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching company autocomplete:', error);
    return [];
  }
}

// Function to fetch matches (queue-driven version)
export async function fetchMatchesFromQueue(queueItems) {
  try {
    console.log('api.fetchMatchesFromQueue - Using queue items:', queueItems.length);
    
    // Filter only completed queue items (disco_done = true)
    const completedItems = queueItems.filter(item => item.disco_done === true);
    
    if (completedItems.length === 0) {
      console.warn('api.fetchMatchesFromQueue - No completed queue items available!');
      return null;
    }
    
    // Construct ICP requirements from completed queue items using only the 4 required fields
    let requirements = completedItems.map(item => ({
      section_type: item.section_type || 'Must Have',
      section_name: item.section_name,
      section_id: item.section_id,
      bool_str: item.bool_str || ''
    }));
    
    // Collapse duplicate fmmagic and skills_gh sections
    requirements = collapseDuplicateFmmagicSections(requirements);
    requirements = collapseDuplicateSkillsGhSections(requirements);
    
    console.log('api.fetchMatchesFromQueue - Constructed requirements from queue items:', requirements.length);
    
    // Add company stage requirements if available
    const companyStageReqs = generateCompanyStageRequirements();
    if (companyStageReqs.length > 0) {
      console.log('Adding company stage requirements to fetchMatchesFromQueue:', companyStageReqs);
      requirements = [...requirements, ...companyStageReqs];
    }
    
    // Add business model requirements if available
    const businessModelReqs = generateBusinessModelRequirements();
    if (businessModelReqs.length > 0) {
      console.log('Adding business model requirements to fetchMatchesFromQueue:', businessModelReqs);
      requirements = [...requirements, ...businessModelReqs];
    }
    
    const response = await fetch('https://pub.findem.ai/pub/api/matches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requirements }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch matches: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in fetchMatchesFromQueue:', error);
    return null;
  }
}

// Function to fetch matches (legacy version)
export async function fetchMatches(requirements) {
  try {
    // Collapse duplicate fmmagic and skills_gh sections before adding additional requirements
    requirements = collapseDuplicateFmmagicSections(requirements);
    requirements = collapseDuplicateSkillsGhSections(requirements);
    
    // Add company stage requirements if available
    const companyStageReqs = generateCompanyStageRequirements();
    if (companyStageReqs.length > 0) {
      console.log('Adding company stage requirements to fetchMatches:', companyStageReqs);
      requirements = [...requirements, ...companyStageReqs];
    }
    
    // Add business model requirements if available
    const businessModelReqs = generateBusinessModelRequirements();
    if (businessModelReqs.length > 0) {
      console.log('Adding business model requirements to fetchMatches:', businessModelReqs);
      requirements = [...requirements, ...businessModelReqs];
    }
    
    const response = await fetch('https://pub.findem.ai/pub/api/matches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requirements }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch matches: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching matches:', error);
    return null;
  }
}

// Function to fetch profiles
export async function fetchProfiles(profileIds) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/profiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ profile_ids: profileIds })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch profiles: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching profiles:', error);
    return [];
  }
}

// Function to run JD matches
export async function runJDMatches(jdText, options = {}) {
  try {
    const payload = {
      jd_text: jdText,
      ...options
    };
    
    const response = await fetch('https://pub.findem.ai/pub/api/jd-matches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to run JD matches: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error running JD matches:', error);
    return null;
  }
}

let cachedOrg = null;
// Function to fetch org preferences
export async function fetchOrgPreferences() {
  if (cachedOrg) {
    return cachedOrg;
  }
  try {
    const response = await fetch('https://jobowners.findem.ai/hm/api/org/prefs', {
      method: 'GET',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch org preferences: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    cachedOrg = data;
    return data;
  } catch (error) {
    console.error('Error fetching org preferences:', error);
    return null;
  }
}

// RAG API Functions

/**
 * Initialize the RAG vector store
 * @returns {Promise<Object>} Initialization result
 */
export async function initializeRAG() {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to initialize RAG: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error initializing RAG:', error);
    return null;
  }
}

/**
 * Search for similar queries using RAG
 * @param {string} userInput - User input to search for
 * @param {number} limit - Maximum number of results to return
 * @returns {Promise<Object>} Search results
 */
export async function searchSimilarQueries(userInput, limit = 5) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/search-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userInput, limit })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to search queries: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching similar queries:', error);
    return null;
  }
}

/**
 * Search for similar industries using RAG
 * @param {string} userInput - User input to search for
 * @param {number} limit - Maximum number of results to return
 * @returns {Promise<Object>} Search results
 */
export async function searchSimilarIndustries(userInput, limit = 5) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/search-industries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userInput, limit })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to search industries: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching similar industries:', error);
    return null;
  }
}

/**
 * Analyze user input using RAG for comprehensive suggestions
 * @param {string} userInput - User input to analyze
 * @returns {Promise<Object>} Analysis results with suggestions
 */
export async function analyzeUserInput(userInput) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userInput })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to analyze input: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error analyzing user input:', error);
    return null;
  }
}

/**
 * Analyze conversation input for free-form intake
 * @param {string} userInput - User input to analyze
 * @param {Array} conversationContext - Previous conversation context
 * @returns {Promise<Object>} Conversation analysis with insights
 */
export async function analyzeConversation(userInput, conversationContext = []) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/conversation-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userInput, conversationContext })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to analyze conversation: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error analyzing conversation:', error);
    return null;
  }
}

/**
 * Get RAG system status
 * @returns {Promise<Object>} RAG system status
 */
export async function getRAGStatus() {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get RAG status: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting RAG status:', error);
    return null;
  }
}

/**
 * Refresh RAG vector stores with latest data
 * @returns {Promise<Object>} Refresh result
 */
export async function refreshRAG() {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/rag/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to refresh RAG: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error refreshing RAG:', error);
    return null;
  }
}

/**
 * Search edit API for copilot
 * @param {string} uid - User ID
 * @param {string} searchQuery - Search query
 * @param {string} editAction - Edit action to perform
 * @returns {Promise<Object>} Parsed API response with structured data
 */
export async function copilotSearchEdit(uid, searchQuery, editAction) {
  try {
    const response = await fetch('https://search.findem.ai/pub/api/copilot_search_edit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        uid: uid,
        search_query: searchQuery,
        edit_action: editAction
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to perform copilot search edit: ${response.status} ${response.statusText}`);
    }

    const rawData = await response.json();
    
    // Parse and structure the response
    const searchEditResponse = rawData.search_edit_response || {};
    const suggestions = searchEditResponse.suggestions || {};
    
    return {
      // Core response metadata
      cxt_id: rawData.cxt_id,
      response_type: rawData.response_type,
      user_query: searchEditResponse.user_query,
      
      // Suggestions data
      suggestions: {
        titles: suggestions.titles || [],
        skills: suggestions.skills || [],
        company_names: suggestions.company_names || {
          generate_company_suggestions: false,
          similar_companies: [],
          exact_companies: [],
          temporal_qualifier: "none"
        },
        actions: suggestions.actions || []
      },
      
      // Change tracking
      additions: searchEditResponse.additions || [],
      removals: searchEditResponse.removals || [],
      inferred_removals: searchEditResponse.inferred_removals || [],
      
      // Metadata
      context_metadata: rawData.search_edit_response?.context_metadata || [],
      total_openai_calls: rawData.search_edit_response?.total_openai_calls || 0,
      
      // Keep raw response for debugging
      raw_response: rawData
    };
  } catch (error) {
    console.error('Error performing copilot search edit:', error);
    throw error;
  }
}

/**
 * Create a copilot project from requirements
 * @param {string} name - Name of the copilot project
 * @param {string} description - Description of the project
 * @param {string} text_prompt - Text prompt for the project
 * @param {Array} requirements - Array of requirements for the project
 * @returns {Promise<Object>} The API response
 */
export async function createCopilotProject(name, description = "Created from intakemate", text_prompt = "Created from intakemate", requirements = []) {
  const url = 'https://search.findem.ai/pub/api/icp';

  // Add company stage requirements if available
  const companyStageReqs = generateCompanyStageRequirements();
  if (companyStageReqs.length > 0) {
    console.log('Adding company stage requirements to createCopilotProject:', companyStageReqs);
    requirements = [...requirements, ...companyStageReqs];
  }

  // Add business model requirements if available
  const businessModelReqs = generateBusinessModelRequirements();
  if (businessModelReqs.length > 0) {
    console.log('Adding business model requirements to createCopilotProject:', businessModelReqs);
    requirements = [...requirements, ...businessModelReqs];
  }

  const headers = {
    'Content-Type': 'application/json'
  };

  const body = {
    name: name,
    search_type: 0,
    description: description,
    mode: "v2",
    // joid: "",
    prioritize_diversity: true,
    locations: [],
    type: "CreateCopilotProjectFromRequirements",
    ss_cxt: {
      sl_options: {
        exclude_company: true,
        exclude_prev_company: true,
        exclude_shortlisted: true,
        exclude_ats: false,
        exclude_crm: false,
        exclude_shortlisted_duration: 0,
        exclude_ats_duration: 0,
        exclude_crm_duration: 0
      }
    },
    text_prompt: text_prompt,
    requirements: requirements,
    is_restricted: false,
    meta: {
      is_smb_campaign: false
    }
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to create copilot project: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Copilot project created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error creating copilot project:', error);
    throw error;
  }
}

// Discovery Details (disco_details) API Functions

/**
 * Get disco details for a specific session
 * @param {string} sessionId - The session ID
 * @param {string} userId - The user ID (optional, defaults to 'default_user')
 * @returns {Promise<Object>} Disco details for the session
 */
export async function getDiscoDetails(sessionId, userId = 'default_user') {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/${userId}/${sessionId}/disco-details`);
    
    if (!response.ok) {
      throw new Error(`Failed to get disco details: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    debugger;
    return data;
  } catch (error) {
    console.error('Error getting disco details:', error);
    return null;
  }
}

/**
 * Helper function to create campaign options with common settings
 * @param {string} user_email - User email
 * @param {string} user_id - User ID
 * @param {Object} campaign_variable_overrides - Campaign variable overrides
 * @param {number} order - Campaign order
 * @param {boolean} auto_select_newshortlist - Auto select new shortlist flag
 * @param {string} template_type - Template type (only for primary campaign)
 * @returns {Object} Campaign options object
 */
function createCampaignOptions(user_email, user_id, campaign_variable_overrides, order, auto_select_newshortlist = false, template_type = undefined) {
  const options = {
    recipient_name: "",
    user_email: user_email,
    exec_mode: 1,
    sender_prid_uid: user_id,
    sender_name: "",
    sender_email: user_email,
    campaign_outreach_mode: "EMAIL_ONLY",
    msg_platform: "Findem",
    sel_mode: 1,
    email_sel_policy: 2,
    auto_select_newshortlist: auto_select_newshortlist,
    min_rating: 1,
    order: order,
    cc_self: false,
    dry_run: false,
    campaign_variable_overrides: campaign_variable_overrides,
    uid: user_id,
    campaign_type: 1,
    disable_multiple_campaigns: true,
    org_unsubscribe_prefs: true,
    unsubscribe_options: {
      unsubscribe_profanity: true,
      unsubscribe_email_content: true
    }
  };

  // Only add template_type if it's defined
  if (template_type !== undefined) {
    options.template_type = template_type;
  }
  return options;
}

/**
 * Assign campaigns to candidates
 * @param {string} icpid - ICP ID from the copilot project
 * @param {string} primary_cid - Primary campaign ID
 * @param {string} job_hopper_cid - Job hopper campaign ID
 * @param {string} non_job_hopper_cid - Non job hopper campaign ID
 * @param {string} interview_cid - Interview campaign ID
 * @param {string} exit_cid - Exit campaign ID
 * @param {string} user_email - User email
 * @param {string} user_id - User ID
 * @param {Object} campaign_variable_overrides - Campaign variable overrides
 * @returns {Promise<Object>} The API response
 */
export async function assignCampaignsToCandidates(
  icpid,
  primary_cid,
  job_hopper_cid,
  non_job_hopper_cid,
  interview_cid,
  exit_cid,
  user_email,
  user_id,
  campaign_variable_overrides = {}
) {
  const url = 'https://sourcing.findem.ai/api/candidates/';

  const headers = {
    'content-type': 'application/json'
  };

  const body = {
    type: "CampaignAssignAsync",
    bulksl_ids: [],
    campaign_id: primary_cid,
    campaign_rules: [
      {
        campaign_id: primary_cid,
        campaign_options: createCampaignOptions(user_email, user_id, campaign_variable_overrides, 0, true, "auto")
      },
      {
        campaign_id: job_hopper_cid,
        campaign_options: createCampaignOptions(user_email, user_id, campaign_variable_overrides, 1, false)
      },
      {
        campaign_id: non_job_hopper_cid,
        campaign_options: createCampaignOptions(user_email, user_id, campaign_variable_overrides, 2, false)
      },
      {
        campaign_id: interview_cid,
        campaign_options: createCampaignOptions(user_email, user_id, campaign_variable_overrides, 3, false)
      },
      {
        campaign_id: exit_cid,
        campaign_options: createCampaignOptions(user_email, user_id, campaign_variable_overrides, 4, false)
      }
    ],
    icpid: icpid,
    joid: user_id,
    prids: [],
    sel_prids: [],
    profile_count: 0,
    start_campaign: false,
    await_async_assign: false
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to assign campaigns to candidates: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Campaigns assigned to candidates successfully:', data);
    return data;
  } catch (error) {
    console.error('Error assigning campaigns to candidates:', error);
    throw error;
  }
}

/**
 * Update disco details for a specific session
 * @param {string} sessionId - The session ID
 * @param {Object} discoDetails - The disco details to update
 * @param {string} userId - The user ID (optional, defaults to 'default_user')
 * @returns {Promise<Object>} Updated disco details
 */
export async function updateDiscoDetails(sessionId, discoDetails, userId = 'default_user') {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/${userId}/${sessionId}/disco-details`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discoDetails })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update disco details: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating disco details:', error);
    return null;
  }
}

/**
 * Start auto sourcing messaging
 * @param {string} joid - Job owner ID (user ID)
 * @param {string} icp_id - ICP ID from the copilot project
 * @param {string} primary_cid - Primary campaign ID
 * @param {string} job_hopper_cid - Job hopper campaign ID
 * @param {string} non_job_hopper_cid - Non job hopper campaign ID
 * @param {string} interview_cid - Interview campaign ID
 * @param {string} exit_cid - Exit campaign ID
 * @param {number} interview_target - Target number of people user wants to interview
 * @param {string} conversation_blob - Stringified JSON of conversation between user and AI agent
 * @param {Array} messaging_qa - Array of objects with question and answer fields
 * @returns {Promise<Object>} The API response
 */
export async function startAutoSourcingMessaging(
  joid,
  icp_id,
  primary_cid,
  job_hopper_cid,
  non_job_hopper_cid,
  interview_cid,
  exit_cid,
  interview_target,
  conversation_blob,
  messaging_qa
) {
  const url = `https://search.findem.ai/pub/api/start_auto_sourcing_messaging?joid=${joid}`;

  const headers = {
    'content-type': 'application/json',
  };

  const body = {
    icpid: icp_id,
    primary_campaign: {
      campaign_id: primary_cid
    },
    job_hopper_campaign: {
      campaign_id: job_hopper_cid
    },
    non_job_hopper_campaign: {
      campaign_id: non_job_hopper_cid
    },
    schedule_interview_campaign: {
      campaign_id: interview_cid
    },
    exit_campaign: {
      campaign_id: exit_cid
    },
    interview_target: interview_target,
    conversation_blob: conversation_blob,
    messaging_qa: messaging_qa
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to start auto sourcing messaging: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Auto sourcing messaging started successfully:', data);
    return data;
  } catch (error) {
    console.error('Error starting auto sourcing messaging:', error);
    throw error;
  }
}

/**
 * Get disco details by sessionId only (convenience function)
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} Disco details for the session
 */
export async function getDiscoDetailsBySessionId(sessionId) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/session/${sessionId}/disco-details`);
    
    if (!response.ok) {
      throw new Error(`Failed to get disco details: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    debugger;
    return data;
  } catch (error) {
    console.error('Error getting disco details:', error);
    return null;
  }
}

/**
 * Link ATS Job to ICP
 * @param {string} joid - Job owner ID (user ID)
 * @param {string} icp_id - ICP ID from the copilot project
 * @param {boolean} export_all_campaign_cands_ats - Export all campaign candidates to ATS
 * @param {boolean} export_as_prospect - Export as prospect
 * @param {string} export_emailids_criteria - Email IDs criteria for export
 * @param {boolean} disable_auto_ats_export - Disable auto ATS export
 * @param {Array} job_ids - Array of job IDs
 * @param {Array} export_ats_suboptions - Array of ATS export sub-options
 * @param {Object} default_ats_export_stage - Default ATS export stage object
 * @returns {Promise<Object>} The API response
 */
export async function deleteIcp(icpids) {
  try {
    if (!icpids) {
      throw new Error('deleteIcp: icpids is required');
    }
    const icpidsStr = Array.isArray(icpids) ? icpids.filter(Boolean).join(',') : String(icpids);
    if (!icpidsStr || icpidsStr.trim().length === 0) {
      throw new Error('deleteIcp: icpids resolved to empty string');
    }

    const url = 'https://matches.findem.ai/hm/api/icp';
    const body = {
      type: 'DeleteIcps',
      icpids: icpidsStr
    };

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'referrer': 'https://app-next.findem.ai/'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ICP(s): ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('deleteIcp: Deleted ICP(s):', icpidsStr, data);
    return data;
  } catch (error) {
    console.error('Error deleting ICP(s):', error);
    throw error;
  }
}

export async function handleATSIcpUpdates(
  joid,
  icp_id,
  export_all_campaign_cands_ats = false,
  export_as_prospect = false,
  export_emailids_criteria = "replied",
  disable_auto_ats_export = true,
  job_ids = [],
  export_ats_suboptions = ["notrightnow", "notinterested", "referral", "notamatch", "anyresponse", "interested"],
  default_ats_export_stage = { "stage_id": "Hired", "stage_name": "Hired" }
) {
  const url = 'https://matches.findem.ai/hm/api/icp';

  const headers = {
    'content-type': 'application/json'
  };

  const body = {
    type: "handleATSIcpUpdates",
    joid: joid,
    icp_id: icp_id,
    export_all_campaign_cands_ats: export_all_campaign_cands_ats,
    export_as_prospect: export_as_prospect,
    export_emailids_criteria: export_emailids_criteria,
    disable_auto_ats_export: disable_auto_ats_export,
    job_ids: job_ids,
    export_ats_suboptions: export_ats_suboptions,
    default_ats_export_stage: default_ats_export_stage
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to handle ATS ICP updates: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('ATS ICP updates handled successfully:', data);
    return data;
  } catch (error) {
    console.error('Error handling ATS ICP updates:', error);
    throw error;
  }
}

/**
 * Update disco details by sessionId only (convenience function)
 * @param {string} sessionId - The session ID
 * @param {Object} discoDetails - The disco details to update
 * @returns {Promise<Object>} Updated disco details
 */
export async function updateDiscoDetailsBySessionId(sessionId, discoDetails) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/session/${sessionId}/disco-details`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discoDetails })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update disco details: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating disco details:', error);
    return null;
  }
}

/**
 * Update chat session with disco details
 * @param {string} sessionId - The session ID
 * @param {Object} updates - Updates including discoDetails
 * @returns {Promise<Object>} Updated session
 */
export async function updateChatSessionWithDiscoDetails(sessionId, updates, userId) {
  try {
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sessionId, userId, updates })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update chat session: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating chat session:', error);
    return null;
  }
}

// Discovery Queue API Functions
// 🎯 RECOMMENDED: Use these discovery-progress endpoints as PRIMARY for central queue management
// ✅ Auto-creates sessions, robust error handling, structured data format

/**
 * Get discovery progress (queue items) for a specific session
 * 🎯 RECOMMENDED: Primary endpoint for loading central discovery queue
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} Discovery progress with queue items
 */
export async function getDiscoveryProgress(sessionId) {
  // Create deduplication key based on sessionId
  const deduplicationKey = `getDiscoveryProgress_${sessionId}`;

  // Use deduplication wrapper to prevent concurrent duplicate calls
  return withDeduplication(deduplicationKey, async () => {
    try {
      // 🎯 CRITICAL FIX: Validate sessionId to prevent using session names as IDs
      if (!sessionId || sessionId === 'Untitled Session' || sessionId.includes('Untitled')) {
        console.error('❌ getDiscoveryProgress: Invalid sessionId provided:', sessionId);
        throw new Error('Invalid sessionId - cannot use session name as session ID');
      }
      
      // Try to fetch userId from user info API
      let userId = null;
      try {
        const userInfo = await fetchUserInfo();
        userId = userInfo?.uid;
      } catch (userError) {
        console.log('Could not fetch userId, proceeding without it');
      }
      
      // Build URL with optional userId query parameter
      let url = `${API_CONFIG.url}/api/chat-history/discovery-progress/${sessionId}`;
      if (userId) {
        url += `?userId=${encodeURIComponent(userId)}`;
      }
      
      console.log('✅ getDiscoveryProgress: Using valid sessionId:', sessionId);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to get discovery progress: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting discovery progress:', error);
      return null;
    }
  });
}

/**
 * Update discovery progress (queue items) for a specific session
 * @param {string} sessionId - The session ID
 * @param {Array} items - Array of discovery queue items
 * @param {string} userId - The user ID (optional, will fetch automatically if not provided)
 * @returns {Promise<Object>} Updated discovery progress
 */
export async function updateDiscoveryProgress(sessionId, items, userId) {
  try {
    // 🎯 CRITICAL FIX: Validate sessionId to prevent using session names as IDs
    if (!sessionId || sessionId === 'Untitled Session' || sessionId.includes('Untitled')) {
      console.error('❌ updateDiscoveryProgress: Invalid sessionId provided:', sessionId);
      throw new Error('Invalid sessionId - cannot use session name as session ID');
    }
    
    // If userId is not provided, fetch it from the user info API
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      console.log('No userId provided, fetching from user info API...');
      const userInfo = await fetchUserInfo();
      effectiveUserId = userInfo?.uid || 'default_user';
      console.log('Fetched userId from API:', effectiveUserId);
    }
    
    if (!effectiveUserId) {
      console.error('nouid');
      throw new Error('userId is required for updateDiscoveryProgress');
    }
    
    console.log('✅ updateDiscoveryProgress: Using valid sessionId:', sessionId);
    
    const requestBody = { sessionId, items, userId: effectiveUserId };
    
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/discovery-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update discovery progress: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating discovery progress:', error);
    return null;
  }
}

/**
 * Update a specific queue item with complete replacement (no merging)
 * @param {string} sessionId - The session ID
 * @param {string} queueId - The queue item ID to update
 * @param {Object} updateData - The new item data (will completely replace existing)
 * @param {string} userId - The user ID (optional, defaults to 'default_user')
 * @returns {Promise<Object>} Update result
 */
export async function updateQueueItem(sessionId, queueId, updateData, userId) {
  try {
    if (!sessionId || sessionId === 'Untitled Session' || sessionId.includes('Untitled')) {
      console.error('❌ updateQueueItem: Invalid sessionId provided:', sessionId);
      throw new Error('Invalid sessionId - cannot use session name as session ID');
    }
    
    // If userId is not provided, fetch it from the user info API
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      console.log('No userId provided, fetching from user info API...');
      const userInfo = await fetchUserInfo();
      effectiveUserId = userInfo?.uid || 'default_user';
      console.log('Fetched userId from API:', effectiveUserId);
    }
    
    console.log('🔄 updateQueueItem: Updating item', queueId, 'in session', sessionId);
    
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/discovery-progress/${sessionId}/item/${queueId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: effectiveUserId,
        ...updateData
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update queue item: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ updateQueueItem: Item updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Error updating queue item:', error);
    return null;
  }
}

/**
 * Remove a specific queue item by queue_id
 * @param {string} sessionId - The session ID
 * @param {string} queueId - The queue item ID to remove
 * @param {string} userId - The user ID (optional, defaults to 'default_user')
 * @returns {Promise<Object>} Remove result
 */
export async function removeQueueItem(sessionId, queueId, userId) {
  try {
    if (!sessionId || sessionId === 'Untitled Session' || sessionId.includes('Untitled')) {
      console.error('❌ removeQueueItem: Invalid sessionId provided:', sessionId);
      throw new Error('Invalid sessionId - cannot use session name as session ID');
    }
    
    // If userId is not provided, fetch it from the user info API
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      console.log('No userId provided, fetching from user info API...');
      const userInfo = await fetchUserInfo();
      effectiveUserId = userInfo?.uid || 'default_user';
      console.log('Fetched userId from API:', effectiveUserId);
    }
    
    console.log('🗑️ removeQueueItem: Removing item', queueId, 'from session', sessionId);
    
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/discovery-progress/${sessionId}/item/${queueId}?userId=${effectiveUserId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to remove queue item: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ removeQueueItem: Item removed successfully:', data);
    return data;
  } catch (error) {
    console.error('Error removing queue item:', error);
    return null;
  }
}

/**
 * Add items to the discovery queue for a session
 * @param {string} sessionId - The session ID
 * @param {Array} newItems - Array of new queue items to add
 * @returns {Promise<Object>} Updated discovery progress
 */
export async function addToDiscoveryQueue(sessionId, newItems) {
  try {
    // First get current queue items
    const currentProgress = await getDiscoveryProgress(sessionId);
    const currentItems = currentProgress?.items || [];
    
    // Merge with new items (avoiding duplicates by section_id)
    const existingIds = new Set(currentItems.map(item => item.section_id));
    const itemsToAdd = newItems.filter(item => !existingIds.has(item.section_id));
    const updatedItems = [...currentItems, ...itemsToAdd];
    
    // Update the queue
    return await updateDiscoveryProgress(sessionId, updatedItems);
  } catch (error) {
    console.error('Error adding to discovery queue:', error);
    return null;
  }
}

/**
 * Mark a discovery queue item as completed
 * @param {string} sessionId - The session ID
 * @param {string} sectionId - The section ID to mark as completed
 * @returns {Promise<Object>} Updated discovery progress
 */
export async function markDiscoveryItemCompleted(sessionId, sectionId) {
  try {
    // Get current queue items
    const currentProgress = await getDiscoveryProgress(sessionId);
    const currentItems = currentProgress?.items || [];
    
    // Update the specific item
    const updatedItems = currentItems.map(item => 
      item.section_id === sectionId 
        ? { ...item, disco_done: true }
        : item
    );
    
    // Update the queue
    return await updateDiscoveryProgress(sessionId, updatedItems);
  } catch (error) {
    console.error('Error marking discovery item as completed:', error);
    return null;
  }
}

/**
 * Clear incomplete discovery queue items for a specific topic
 * @param {string} sessionId - The session ID
 * @param {string} topic - The topic to clear incomplete items for
 * @returns {Promise<Object>} Updated discovery progress
 */
export async function clearIncompleteItemsByTopic(sessionId, topic) {
  try {
    if (!sessionId || sessionId === 'Untitled Session' || sessionId.includes('Untitled')) {
      console.error('❌ clearIncompleteItemsByTopic: Invalid sessionId provided:', sessionId);
      throw new Error('Invalid sessionId - cannot use session name as session ID');
    }

    // Try to fetch userId from user info API
    let userId = null;
    try {
      const userInfo = await fetchUserInfo();
      userId = userInfo?.uid;
    } catch (userError) {
      console.log('Could not fetch userId, proceeding without it');
    }

    const requestBody = { sessionId, topic, userId };
    
    const response = await fetch(`${API_CONFIG.url}/api/chat-history/discovery-progress/${sessionId}/clear-incomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to clear incomplete items: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error clearing incomplete items by topic:', error);
    return null;
  }
}

/**
 * Get the next pending item from the discovery queue
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object|null>} Next pending queue item or null if none
 */
export async function getNextPendingDiscoveryItem(sessionId) {
  try {
    const progress = await getDiscoveryProgress(sessionId);
    const items = progress?.items || [];
    
    // Find the first item that's not completed
    const nextItem = items.find(item => !item.disco_done);
    return nextItem || null;
  } catch (error) {
    console.error('Error getting next pending discovery item:', error);
    return null;
  }
}

/**
 * Get discovery progress statistics for a session
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} Progress statistics
 */
export async function getDiscoveryProgressStats(sessionId) {
  try {
    const progress = await getDiscoveryProgress(sessionId);
    const items = progress?.items || [];
    
    const total = items.length;
    const completed = items.filter(item => item.disco_done).length;
    const pending = total - completed;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return {
      total,
      completed,
      pending,
      progressPercent,
      items
    };
  } catch (error) {
    console.error('Error getting discovery progress stats:', error);
    return {
      total: 0,
      completed: 0,
      pending: 0,
      progressPercent: 0,
      items: []
    };
  }
}

/**
 * Fetch company list suggestions from copilot API
 * @param {string} companyListDescription - Description of companies to find (e.g., "top 50 saas companies")
 * @returns {Promise<Array>} Array of company names
 */
export async function fetchCopilotCompanyList(companyListDescription) {
  try {
    const response = await fetch("https://search.findem.ai/pub/api/copilot-company-list", {
      headers: {
        "content-type": "application/json"
      },
      referrer: "https://app-next.findem.ai/",
      body: JSON.stringify({
        company_list_info: {
          company_list: companyListDescription
        }
      }),
      method: "POST",
      credentials: "include"
    });

    if (!response.ok) {
      throw new Error(`Copilot company list API failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    
    // Return the company list array directly
    return result.company_list_response?.company_list || [];
  } catch (error) {
    console.error('Error fetching copilot company list:', error);
    throw error;
  }
}
